import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Activity, Clock, Search, Filter, RefreshCw, Download } from 'lucide-react';
import { ActivityLog } from '../types';
import { fetchLogs } from '../utils/authApi';
import { getPermissions } from '../utils/permissions';
import * as XLSX from 'xlsx';

interface LogsViewProps {
  userRole?: string;
  authUserPerms?: Record<string, boolean>;
  outletId?: number | null;
}

export default function LogsView({ userRole, authUserPerms, outletId = null }: LogsViewProps) {
  const perms = getPermissions(userRole, authUserPerms, undefined, undefined, undefined, null);

  if (!perms.canAccessLogs) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '80px',
        color: '#9ca3af',
        fontSize: 14,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        You don't have permission to view audit logs.
      </div>
    );
  }

 const [logs, setLogs] = useState<ActivityLog[]>([]);
const [searchTerm, setSearchTerm] = useState('');
const [backupWarning, setBackupWarning] = useState(false);
const [expiringCount, setExpiringCount] = useState(0);

const loadLogs = () => {
  fetchLogs(outletId).then((data) => {
    setLogs(data.logs);
    setBackupWarning(data.backup_warning);
    setExpiringCount(data.expiring_count);
  }).catch(console.error);
};

useEffect(() => {
  loadLogs();
  const interval = setInterval(loadLogs, 30000);
  return () => clearInterval(interval);
}, [outletId]);


  const [actionCategory, setActionCategory] = useState<'all' | 'SYSTEM' | 'ACCOUNT' | 'INVOICE' | 'DRIVE'>('all');

  const filteredLogs = logs.filter((l) => {
    const matchesSearch = l.details.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          l.action.toLowerCase().includes(searchTerm.toLowerCase());
                          
    const matchesCategory = actionCategory === 'all' || l.action.includes(actionCategory);

    return matchesSearch && matchesCategory;
  });
const exportCSV = () => {
  const rows = filteredLogs.map(l => ({
    'Date & Time': new Date(l.timestamp.endsWith('Z') || l.timestamp.includes('+') ? l.timestamp : l.timestamp + 'Z').toLocaleString(),
    'Action': l.action,
    'Details': l.details,
  }));
  const csv = [
    Object.keys(rows[0]).join(','),
    ...rows.map(r => Object.values(r).map(v => `"${v}"`).join(','))
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-logs-${Date.now()}.csv`;
  a.click();
};

const exportExcel = () => {
  const rows = filteredLogs.map(l => ({
    'Date & Time': new Date(l.timestamp.endsWith('Z') || l.timestamp.includes('+') ? l.timestamp : l.timestamp + 'Z').toLocaleString(),
    'Action': l.action,
    'Details': l.details,
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Audit Logs');
  XLSX.writeFile(wb, `audit-logs-${Date.now()}.xlsx`);
};

const exportPDF = () => {
  const rows = filteredLogs.map(l =>
    `${new Date(l.timestamp.endsWith('Z') || l.timestamp.includes('+') ? l.timestamp : l.timestamp + 'Z').toLocaleString()} | ${l.action} | ${l.details}`
  ).join('\n');
  const blob = new Blob([`AUDIT LOGS EXPORT\n\n${rows}`], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-logs-${Date.now()}.txt`;
  a.click();
};
  return (
    <>
    <style>{`
      .logs-scroll {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .logs-scroll::-webkit-scrollbar {
        width: 0px;
        height: 0px;
        display: none;
      }
    `}</style>
    <div
      className="flex-1 overflow-y-auto bg-slate-50/50 p-6 space-y-6 select-none logs-scroll"
      style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      
      {/* HEADER BAR */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-xl font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            <span>Operational Audit Logs Ledger</span>
          </h1>
          <p className="text-xs text-slate-450 mt-1 leading-normal">
            Automated system action history tracking. Trace security auth states, billing changes, client profiles creation, and Drive synchronization events.
          </p>
        </div>

       {logs.length > 0 && (
  <div className="flex items-center gap-2 flex-wrap self-start sm:self-auto">
    {perms.canExportExcel && (
      <button onClick={exportCSV} className="px-3.5 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold rounded text-xs transition-all flex items-center gap-1.5 cursor-pointer">
        <Download className="w-3.5 h-3.5" />
        <span>CSV</span>
      </button>
    )}
    {perms.canExportExcel && (
      <button onClick={exportExcel} className="px-3.5 py-1.5 border border-emerald-200 hover:bg-emerald-50 text-emerald-700 font-bold rounded text-xs transition-all flex items-center gap-1.5 cursor-pointer">
        <Download className="w-3.5 h-3.5" />
        <span>Excel</span>
      </button>
    )}
    {perms.canExportPDF && (
      <button onClick={exportPDF} className="px-3.5 py-1.5 border border-blue-200 hover:bg-blue-50 text-blue-700 font-bold rounded text-xs transition-all flex items-center gap-1.5 cursor-pointer">
        <Download className="w-3.5 h-3.5" />
        <span>PDF</span>
      </button>
    )}
 
  </div>
)}
      </div>

      {/* BACKUP WARNING BANNER */}
      {backupWarning && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3">
          <span className="text-amber-500 text-xl">🔥</span>
          <div className="flex-1">
            <p className="text-amber-800 font-bold text-sm">Audit Logs Expiring Tomorrow</p>
            <p className="text-amber-700 text-xs mt-0.5">
              {expiringCount} log(s) will be permanently deleted tomorrow as they have reached the 6-month retention limit. Export your logs now before they are gone.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={exportCSV} className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded text-xs transition-all cursor-pointer">
              Export CSV
            </button>
            <button onClick={exportExcel} className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded text-xs transition-all cursor-pointer">
              Export Excel
            </button>
            <button onClick={() => setBackupWarning(false)} className="ml-1 text-amber-400 hover:text-amber-700 font-bold text-lg leading-none cursor-pointer">
              ✕
            </button>
          </div>
        </div>
      )}

      {/* FILTER CONTROLS */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        
        {/* Search Input */}
        <div className="relative w-full md:w-80 border-slate-200">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search details text logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-505"
          />
        </div>
        {/* Filter Selection Tabs */}
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider hidden lg:inline">Filter By:</span>
          
          <div className="flex flex-wrap items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1">
            {[
              { id: 'all', label: 'All Log Details' },
              { id: 'SYSTEM', label: 'System Action' },
              { id: 'ACCOUNT', label: 'Client Accounts' },
              { id: 'INVOICE', label: 'Invoice claim' },
              { id: 'DRIVE', label: 'Drive Sync' }
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActionCategory(cat.id as any)}
                className={`px-2.5 py-1.5 rounded text-[10.5px] font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
                  actionCategory === cat.id
                    ? 'bg-slate-850 text-white shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* DATA LEDGER TABLE */}
<div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto logs-scroll">
  <table className="w-full text-left border-collapse text-xs min-w-[600px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 font-extrabold text-slate-600 uppercase tracking-wider text-[10px]">
              <th className="p-4 w-52"><span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Logged Date & Time</span></th>
              <th className="p-4 w-44">Action Event Tag</th>
              <th className="p-4 w-40">By</th>
              <th className="p-4">Detailed Descriptive Status Narrative</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-150 text-slate-700 font-sans font-medium">
            {filteredLogs.map((l) => (
              <motion.tr
                key={l.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
                className="hover:bg-slate-50/50 transition-colors"
              >
                
                {/* Time */}
                <td className="p-4 font-mono text-slate-450 text-[11px] whitespace-nowrap">
                  {new Date(l.timestamp.endsWith('Z') || l.timestamp.includes('+') ? l.timestamp : l.timestamp + 'Z').toLocaleString()}
                </td>

                {/* Event tag */}
                <td className="p-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${
                    l.action.includes('SYSTEM') ? 'bg-blue-50 border-blue-200 text-blue-700' :
                    l.action.includes('INVOICE') ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                    l.action.includes('ACCOUNT') ? 'bg-sky-50 border-sky-200 text-sky-700' :
                    'bg-slate-100 border border-slate-200 text-slate-600'
                  }`}>
                    {l.action}
                  </span>
                </td>

                {/* By — who performed the action */}
                <td className="p-4 whitespace-nowrap">
                  <span className="text-slate-700 font-bold text-xs">{l.user_name || '—'}</span>
                  {l.user_role && (
                    <span className="ml-1.5 text-[10px] text-slate-400 font-semibold uppercase">({l.user_role})</span>
                  )}
                </td>

                {/* Description info */}
                <td className="p-4 text-slate-600 leading-normal font-sans font-semibold">
                  {l.details}
                </td>

              </motion.tr>
            ))}

            {filteredLogs.length === 0 && (
              <tr>
                <td colSpan={3} className="p-8 text-center text-slate-400 font-sans font-medium">
                  No tracking logs parsed matching filter criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  </>
  );
}
